import * as feedActions from '../../actions/feed.actions';
import * as widgetActions from '../../actions/widget.actions';

import { Actions, ofType } from '@ngrx/effects';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  shareReplay,
  take,
  tap,
} from 'rxjs';
import { Feed, Post, User, Widget } from 'src/app/core/models/core.models';
import {
  FeedPostViewMode,
  WidgetViewProperties,
} from 'src/app/api/services/feed-api.service';
import {
  NomDateTimeFormats,
  NomObjectClass,
  NomObjectTypes,
  WidgetType,
} from '../../constants';
import {
  SidebarButtonBehavior,
  SidebarButtonConfig,
} from '../../components/sidebar/sidebar.component';
import { Store } from '@ngrx/store';
import {
  clearSelectedObjects,
  selectObjects,
} from '../../actions/selected.actons';
import { objectChanged, periodChanged } from '../../actions/core.actions';
import { openPostDetails } from '../../actions/post.actions';
import {
  selectAppliedAlgorithmPostIds,
  selectCountSelectedWidgets,
  selectCurrentObject,
  selectCurrentObjectEditMode,
  selectExportSchedulesCount,
  selectFeed,
  selectFeedAlertsCount,
  selectFeedCurrentPage,
  selectFeedItemsPerPage,
  selectFeedSelectedFilterItems,
  selectFeedTimeShown,
  selectIsLoadingWidgets,
  selectLoadingAlgorithmPostIds,
  selectObjectIsExporting,
  selectObjectWidgetViewProperties,
  selectSelectedPlaceholderId,
  selectSelectedPostId,
  selectUser,
  selectWidgetSearchString,
  selectWidgetsLanguageId,
} from '../../reducers';

import { ActivatedRoute } from '@angular/router';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { FormGroup } from '@angular/forms';
import { FormsService } from '../../services/forms.service';
import { GridStackNode, GridStackOptions } from 'gridstack';
import { GridstackComponent } from 'gridstack/dist/angular';
import { GridsterConfigService } from '../../services/gridster-config.service';
import { LoggerService } from '@services/logger.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { PanelComponentMappingService } from '../../services/panel-component-mapping.service';
import { PostListComponent } from '../../components/post-list/post-list.component';
import { PropertyViewConfig } from '../../services/feed-settings.service';
import { SearchBuilderData } from '../../components/search-builder/search-builder.component';
import { ShareMode } from '../../components/popups/share-dialog/share-dialog.component';
import { SidebarService } from '../../services/sidebar.service';
import { SortableResult } from '../../services/sortable-engine.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { UtilsService } from '../../services/utils.service';
import { WidgetService } from '../../services/widget.service';
import { clearSelectedPrincipals } from '../../actions/principals.actions';
import cloneDeep from 'lodash-es/cloneDeep';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { FeedPostsService } from '../../services/feed-posts.service';
import { injectFeedPostsQuery } from 'src/app/api/queries/feed.queries';
import { firstCanonicalPost } from 'src/app/shared/utils/post-thread.utils';
import {
  ObjectWidgetsScope,
  allWidgetDataQueriesFilter,
  injectObjectWidgetsQuery,
} from 'src/app/api/queries/widget.queries';
import { injectFeedViewConfigQuery } from 'src/app/api/queries/view-preset.queries';
import {
  applyStagedLayout,
  filterWidgetsBySearchString,
  sortWidgetsByPosition,
} from 'src/app/shared/utils/widget-list.utils';
import { WidgetViewPresetService } from '../../services/widget-view-preset.service';
import { FeedAlgorithmsService } from '../../services/feed-algorithms.service';
import { FeedViewsService } from '../../services/feed-views.service';
import { WidgetLayoutStagingService } from '../../services/widget-layout-staging.service';
import { WidgetPersistenceService } from '../../services/widget-persistence.service';
import { RecentObjectsService } from '../../services/recent-objects.service';
import { FeedSettingsAutosaveService } from '../../services/feed-settings-autosave.service';

@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
// Represents the FeedComponent class.
// This component is responsible for displaying and managing the feed, including posts and widgets.
// It handles user interactions and communicates with the store to retrieve and update data.
export class FeedComponent
  implements OnDestroy, AfterViewInit, AfterViewChecked
{
  private readonly queryClient = inject(QueryClient);
  private readonly layoutStaging = inject(WidgetLayoutStagingService);
  private readonly widgetViewPreset = inject(WidgetViewPresetService);
  private readonly feedAlgorithms = inject(FeedAlgorithmsService);
  private readonly feedViews = inject(FeedViewsService);
  private readonly widgetPersistence = inject(WidgetPersistenceService);
  private readonly recentObjects = inject(RecentObjectsService);
  // Injected for its side effects only: instantiating it starts the settings
  // autosave pipeline, and a feed being open is when settings can change.

  // ---- the feed's widget list ------------------------------------------------
  // A feed's list has no page, so `pageId` is null; see `objectWidgetsQueryKey` on
  // why that cannot collide with Presentation Mode's entry.
  private readonly widgetsScope = signal<ObjectWidgetsScope | null>(null);
  private readonly widgetSearchQuery = signal('');
  private readonly widgetsLanguageId = signal<number | null>(null);

  readonly widgetsQuery = injectObjectWidgetsQuery(this.widgetsScope);

  // The scope comes from `FeedPostsService` rather than being rebuilt here, so the
  // key this observes is provably the one the service's patches target.
  private readonly feedPosts = inject(FeedPostsService);
  readonly postsQuery = injectFeedPostsQuery(this.feedPosts.scope);

  /** `dataUpdatedAt` of the last result set broadcast as `feedPostsLoaded`. */
  private postsLoadedAt = 0;

  // Read (untracked) when a fresh result set lands, to clamp the page.
  private readonly currentPageValue = this.store.selectSignal(
    selectFeedCurrentPage,
  );
  private readonly itemsPerPageValue = this.store.selectSignal(
    selectFeedItemsPerPage,
  );

  // `feed-results-toolbar` owns the same query; the shared key makes the two
  // observers one request.
  private readonly viewConfigQuery = injectFeedViewConfigQuery(
    this.store.selectSignal(selectFeed),
    this.store.selectSignal(selectUser),
  );

  /**
   * What the grid renders: the fetched list with un-persisted drag/resize
   * geometry merged over it, sorted, then narrowed by the widget search.
   */
  private readonly widgetList = computed(() =>
    filterWidgetsBySearchString(
      sortWidgetsByPosition(
        applyStagedLayout(
          this.widgetsQuery.data() ?? [],
          this.layoutStaging.layout(),
        ),
      ),
      this.widgetSearchQuery(),
      this.widgetViewPreset.editMode(),
      this.widgetsLanguageId(),
    ),
  );

  @ViewChild('sidebar') sidebar: ElementRef;

  private _gridstack: GridstackComponent;
  @ViewChild(GridstackComponent)
  set gridstack(value: GridstackComponent) {
    this._gridstack = value;
    if (value) {
      this.setupGridstackEventListeners();
    }
  }
  get gridstack(): GridstackComponent {
    return this._gridstack;
  }

  @ViewChild('postList') postList: PostListComponent;

  // Grid configuration from service
  gridstackOptions: GridStackOptions;

  // * track which Widget is currently being resized
  resizingWidgetId$ = new BehaviorSubject<string | null>(null);
  // * track if any widget is currently being dragged
  isDragging$ = new BehaviorSubject<boolean>(false);

  feed$: Observable<Feed>;
  objectViewProperties$: Observable<WidgetViewProperties>;
  posts$: Observable<Array<Post>>;
  widgets$: Observable<Array<Widget>>;
  widgetsWithGridOptions$: Observable<
    Array<{ widget: Widget; gridOptions: GridStackNode }>
  >;
  selectedPostId$: Observable<string>;
  postsLoading$: Observable<boolean>;
  widgetsSortableList: Widget[] = [];
  widgetsLoading$: Observable<boolean>;
  isDownlaoding$: Observable<boolean>;
  countSelectedWidgets$: Observable<number>;
  currentObjectEditMode$: Observable<boolean>;
  selectSelectedPlaceholderId$: Observable<string>;
  filteredSidebarButtons$: Observable<SidebarButtonConfig[]>;
  loadingAlgorithmPostIds$: Observable<string[]>;
  appliedAlgorithmPostIds$: Observable<string[]>;
  currentPage$: Observable<number>;
  itemsPerPage$: Observable<number>;
  totalItemsCount$: Observable<number>;
  searchWidgets$ = new Subject<string>();

  private resizeObserver: ResizeObserver;
  private refreshWidgetList$ = new Subject<void>();
  private sidebarButtons$ = new BehaviorSubject<SidebarButtonConfig[]>([]);
  private gridOptionsCache = new Map<string, GridStackNode>();

  user: User;
  feed: Feed;
  feedId: string;
  isStoryFeed = false;
  selectedPostId: string;
  widgetEditMode: boolean;
  debouncedSearchPosts: any;
  debouncedSearchWidgets: any;
  searchBuilderData: SearchBuilderData;
  sidebarButtons: SidebarButtonConfig[];
  feedViewConfig: PropertyViewConfig[];
  timeShown: string;
  feedSearchQuery: string;
  widgetSearchString: string;
  intervalId: any;

  gridInitialized = false;
  // Set at the start of ngOnDestroy so the feed$ subscription can tell a
  // genuine "feed not found" apart from the expected null emitted when
  // `feedUnloaded` resets the store during teardown (navigate-away).
  private isDestroyed = false;

  WidgetType = WidgetType;
  NomDateTimeFormats = NomDateTimeFormats;
  paneClass = 'pane-default';
  viewMode: FeedPostViewMode = 'default';
  validViewModes: FeedPostViewMode[] = [
    'default',
    'threads',
    'compact',
    'gallery',
  ];
  currentLanguage = this.translateService.currentLang;
  private pendingScrollWidgetId: string | null = null;

  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  private advancedSearchService = inject(AdvancedSearchService);

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.altKey && event.key.toLowerCase() === 'r') {
      event.preventDefault();
      this.feedRefresh();
    }
  }

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private sidebarPanelService: PanelComponentMappingService,
    private activatedRoute: ActivatedRoute,
    private formService: FormsService,
    public utilsService: UtilsService,
    public objectActionService: ObjectActionsService,
    public widgetService: WidgetService,
    public sidebarService: SidebarService,
    private translateService: TranslateService,
    private logger: LoggerService,
    public userFacade: UserFacadeService,
    private gridsterConfigService: GridsterConfigService,
    private objectEditorService: ObjectEditorService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
  ) {
    // Injected for its side effect only: the service starts autosaving feed settings on construction
    inject(FeedSettingsAutosaveService);
    // Initialize grid configuration from service
    this.gridstackOptions = this.gridsterConfigService.getFeedConfig();

    this.feed$ = this.store.select(selectFeed);
    this.objectViewProperties$ = this.store.select(
      selectObjectWidgetViewProperties,
    );
    this.selectedPostId$ = this.store.select(selectSelectedPostId);
    this.countSelectedWidgets$ = this.store.select(selectCountSelectedWidgets);
    this.isDownlaoding$ = this.store.select(selectObjectIsExporting);
    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );

    this.selectedPostId$
      .pipe(
        tap((id) => {
          this.selectedPostId = id;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    toObservable(computed(() => this.viewConfigQuery.data()?.config ?? []))
      .pipe(
        tap((config) => {
          this.logger.debug('Setting feedViewConfig', config);
          this.feedViewConfig = config;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.selectSelectedPlaceholderId$ = this.store.select(
      selectSelectedPlaceholderId,
    );

    this.posts$ = toObservable(
      computed(() => [...(this.postsQuery.data()?.posts.values() ?? [])]),
    );

    this.postsLoading$ = toObservable(
      computed(() => this.postsQuery.isLoading()),
    );

    // The details pane's default post: the first non-parent entry in insertion
    // order is the response's first post.
    //
    // Gated on `dataUpdatedAt`, which `FeedPostsService` deliberately leaves alone
    // when it patches - otherwise every checkbox click would look like a fresh
    // result set and reset the pane to the first post.
    effect(() => {
      const data = this.postsQuery.data();
      const loadedAt = this.postsQuery.dataUpdatedAt();

      if (!data || loadedAt === this.postsLoadedAt) return;
      this.postsLoadedAt = loadedAt;

      this.store.dispatch(
        feedActions.feedPostsLoaded({
          postId: firstCanonicalPost(data.posts)?.id ?? null,
        }),
      );

      // When the result count shrinks (e.g. categorizing away every post on the
      // last page), the stored page can point past the end of the collection.
      // Redirect to the last valid page; `pageChanged` re-fetches and syncs the
      // `?page=` URL param through the existing effects. Converges because the
      // page strictly decreases on every correction.
      untracked(() => {
        // `page` may arrive as a string via the `?page=` route param
        const page = Number(this.currentPageValue()) || 1;
        const itemsPerPage = Number(this.itemsPerPageValue()) || 1;
        const lastValidPage = Math.max(
          1,
          Math.ceil(data.totalItemsCount / itemsPerPage),
        );
        if (page > lastValidPage) {
          this.store.dispatch(
            feedActions.pageChanged({ pageNumber: lastValidPage }),
          );
        }
      });
    });
    // The NgRx flag is only raised by a bulk delete; the list's own loading state
    // comes from the query.
    this.widgetsLoading$ = combineLatest([
      this.store.select(selectIsLoadingWidgets),
      toObservable(computed(() => this.widgetsQuery.isLoading())),
    ]).pipe(map(([deleting, loadingList]) => deleting || loadingList));

    this.currentPage$ = this.store.select(selectFeedCurrentPage);
    this.itemsPerPage$ = this.store.select(selectFeedItemsPerPage);
    this.totalItemsCount$ = toObservable(
      computed(() => this.postsQuery.data()?.totalItemsCount ?? 0),
    );

    // * Shared observable that replays the last emitted value to new subscribers
    this.widgets$ = toObservable(this.widgetList).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // The scope drives the list query's key, so a new feed refetches. Derived from
    // `selectCurrentObject` rather than the route param so it is *provably* the same
    // key `CurrentViewWidgetsService` builds for the services that read this list.
    this.store
      .select(selectCurrentObject)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((object) => {
        this.widgetsScope.set(
          object?.id
            ? {
                objectId: object.id,
                objectClassId: object.object_class_id,
                pageId: null,
              }
            : null,
        );
      });

    // The three client-state inputs of the widget search filter.
    combineLatest([
      this.store.select(selectWidgetSearchString),
      this.store.select(selectWidgetsLanguageId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchString, languageId]) => {
        this.widgetSearchQuery.set(searchString ?? '');
        this.widgetsLanguageId.set(languageId);
      });

    // * Pre-built, reference-stable gridstack options per widget: the
    // * gridstack-item [options] setter calls grid.update() whenever the input
    // * reference changes, so an inline template literal would re-layout every
    // * widget on every change-detection cycle.
    this.widgetsWithGridOptions$ = this.widgets$.pipe(
      map((widgets) => {
        const cache = new Map<string, GridStackNode>();
        const items = widgets.map((widget) => {
          const gridOptions = this.toGridOptions(widget);
          cache.set(widget.id, gridOptions);
          return { widget, gridOptions };
        });
        this.gridOptionsCache = cache;
        return items;
      }),
    );

    this.searchWidgets$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap((query) => {
          this.widgetSearchString = query;
          this.store.dispatch(widgetActions.searchWidgets({ query }));
          // * the sortable list only refreshes on-demand — sync it with the
          // * filtered store state (sorting itself is disabled while searching)
          if (this.widgetEditMode) {
            this.refreshWidgetList$.next();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * seed fixed array for SortableListDirective ONCE! (avoid stream overwrites during drag)
    // * refreshes on-demand via refreshWidgetList$ trigger
    this.refreshWidgetList$
      .pipe(
        tap(() => {
          this.widgetsSortableList = [...this.widgetList()]
            .sort((a, b) => a.y - b.y)
            .filter(
              (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
            );

          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.feed$
      .pipe(
        tap((feed) => {
          if (!feed) {
            // Skip during teardown: `feedUnloaded` (dispatched in ngOnDestroy)
            // resets the store to a null feed, which is expected — not an error.
            if (!this.isDestroyed) {
              this.logger.error('Feed not found');
            }
            return;
          }
          this.feed = feed;
          this.isStoryFeed = feed.object_type_id === NomObjectTypes.STORY_FEED;
          this.estimatePaneClass(feed);
          // Assuming that this.feed.view_posts_mode might be any string, we validate it.
          this.viewMode = this.validViewModes.includes(
            this.feed.view_posts_mode,
          )
            ? (this.feed.view_posts_mode as FeedPostViewMode)
            : 'default';
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * Re-arm the auto-refresh timer only when its inputs change. The feed
    // * object is replaced on every interaction (Immer - even a post selection
    // * bumps count_selected_posts), and re-arming per emission restarted the
    // * countdown so it never fired while the user was working (WAP-1584).
    this.feed$
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a?.id === b?.id &&
            a?.is_auto_refresh === b?.is_auto_refresh &&
            a?.auto_refresh_seconds === b?.auto_refresh_seconds,
        ),
        tap((feed) => {
          if (feed) {
            this.setupAutoRefresh(feed);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.data
      .pipe(
        tap((data) => {
          if (data['feed']) {
            this.store.dispatch(objectChanged({ object: data['feed'] }));
            this.recentObjects.record(data['feed']);
            this.setupSidebar(data['feed']);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.params
      .pipe(
        tap((params) => {
          const feedId = params['feedId'];
          this.feedId = feedId;

          // No posts or algorithms dispatch: both queries are keyed on the feed,
          // so navigating to another one *is* the reload.
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.filteredSidebarButtons$ = combineLatest([
      this.sidebarButtons$,
      this.store.select(selectUser),
      this.store.select(selectFeedSelectedFilterItems),
      this.store.select(selectFeedAlertsCount),
      this.store.select(selectExportSchedulesCount),
      toObservable(this.feedAlgorithms.attached),
    ]).pipe(
      map(
        ([
          buttons,
          user,
          activeFilterItems,
          alertsCount,
          exportSchedulesCount,
          attachedAlgorithms,
        ]) => {
          return buttons
            ?.map((button) => {
              const updatedButton = { ...button };

              // * add counter for filters
              if (button.key === 'filters') {
                updatedButton.counter = activeFilterItems.length;
              }

              if (button.key === 'alerts') {
                updatedButton.counter = alertsCount;
              }

              if (button.key === 'schedules') {
                updatedButton.counter = exportSchedulesCount;
              }

              if (button.key === 'algorithms') {
                updatedButton.counter = attachedAlgorithms.length;
              }

              return updatedButton;
            })
            .filter((button) => button.shouldDisplay !== false);
        },
      ),
    );

    this.store
      .select(selectCurrentObject)
      .pipe(
        tap((object) => {
          const shouldHideSearch =
            object?.object_type_id === NomObjectTypes.CLIPPING_FEED;
          // Update sidebar buttons' display flag based on header value.
          this.sidebarButtons?.forEach((button) => {
            button.shouldDisplay =
              button.key === 'search' ? !shouldHideSearch : true;
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectFeedTimeShown)
      .pipe(
        tap((timeShown) => {
          this.timeShown = timeShown;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loadingAlgorithmPostIds$ = this.store.select(
      selectLoadingAlgorithmPostIds,
    );
    this.appliedAlgorithmPostIds$ = this.store.select(
      selectAppliedAlgorithmPostIds,
    );

    this.advancedSearchService.searchQuery$
      .pipe(
        tap((query) => {
          this.feedSearchQuery = query;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(
          widgetActions.createWidgetSuccess,
          widgetActions.updateWidgetSuccess,
          widgetActions.deleteWidgetSuccess,
          widgetActions.deleteWidgetsSuccess,
          widgetActions.massUpdateWidgetsSuccess,
        ),
        tap(() => {
          // Only refresh if we're currently in edit mode
          if (this.widgetEditMode) {
            this.refreshWidgetList$.next();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    toObservable(computed(() => this.widgetsQuery.data()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((widgets) => {
        if (widgets && this.widgetEditMode) {
          this.refreshWidgetList$.next();
        }
      });

    // A plain field mirroring the `pv-widgets` blob - the template and four call
    // sites read it directly.
    toObservable(this.widgetViewPreset.editMode)
      .pipe(
        tap((editMode: boolean) => {
          this.widgetEditMode = editMode;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    // * trigger initial widget list load
    this.refreshWidgetList$.next();

    this.setupRouteParamsHandling();
    this.setupWidgetCreationListener();
    this.setupFeedChangeListener();

    window.addEventListener('message', this.handleIFrameMessage);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.currentLanguage = language.lang;
      });

    // Calculate initial cell height and set up resize listener
    setTimeout(() => {
      this.calculateCellHeight();
    });

    // Recalculate on window resize
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        tap(() => this.calculateCellHeight()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const open = params['open'];
        if (!open || !this.feed) {
          return;
        }
        if (open == 'alerts') {
          const alertsButton = this.sidebarButtons?.find(
            (button) => button.key === 'alerts',
          );
          if (alertsButton) {
            this.sidebarService.selectComponent(alertsButton, true);
          }
        } else if (open == 'export-schedules') {
          const schedulesButton = this.sidebarButtons?.find(
            (button) => button.key === 'schedules',
          );
          if (schedulesButton) {
            this.sidebarService.selectComponent(schedulesButton, true);
          }
        }
      });
  }

  ngAfterViewChecked(): void {
    // ! check if we need to scroll to a newly created widget
    if (this.pendingScrollWidgetId) {
      const widgetElement = document.getElementById(
        `widget-${this.pendingScrollWidgetId}`,
      );
      if (widgetElement) {
        this.scrollToWidget(this.pendingScrollWidgetId);
        this.pendingScrollWidgetId = null;
      }
    }
  }

  ngOnDestroy(): void {
    // Mark teardown BEFORE dispatching feedUnloaded — that dispatch synchronously
    // resets the store, pushing a null feed through the still-live feed$ subscription.
    this.isDestroyed = true;
    // Dispatch an action when the component is destroyed
    this.logger.debug('Feed component destroyed');
    this.store.dispatch(feedActions.feedUnloaded());
    if (this.feed) {
      this.store.dispatch(
        clearSelectedPrincipals({ companyId: this.feed.company_id }),
      );
    }

    this.sidebarService.closeSidebar();
    // Remove the event listener to prevent memory leaks
    window.removeEventListener('message', this.handleIFrameMessage);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearInterval(this.intervalId);
  }

  private setupGridstackEventListeners(): void {
    if (!this.gridstack?.grid) {
      return;
    }

    // ! this is the better way to track the Widget that is being resized
    // ! do not try with changeCB resize start/end events - it will not work as expected
    this.gridstack.grid.on('resizestart', (event, el) => {
      const widgetId = el.gridstackNode?.id;
      if (widgetId) {
        this.resizingWidgetId$.next(widgetId.toString());
      }
    });

    this.gridstack.grid.on('resizestop', (event, el) => {
      this.resizingWidgetId$.next(null);
    });

    this.gridstack.grid.on('dragstart', (event, el) => {
      this.isDragging$.next(true);
    });

    this.gridstack.grid.on('dragstop', (event, el) => {
      this.isDragging$.next(false);
    });
  }

  setupSidebar(feed: Feed) {
    const buttons: SidebarButtonConfig[] = [
      {
        label: this.translateService.instant('sidebar-tools.label.settings'),
        key: 'settings',
        header: this.translateService.instant('sidebar-tools.label.settings'),
        icon: ['fas', 'gear'],
        behaviorType: SidebarButtonBehavior.ACTION,
        actionHandler: () => {
          this.objectEditorService.loadFeedAndOpenPopup(feed.id);
        },
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant(
          'sidebar-tools.label.search-builder',
        ),
        componentName: 'SearchBuilderWrapperComponent',
        header: this.translateService.instant(
          'sidebar-tools.label.search-builder',
        ),
        key: 'search-builder',
        icon: ['fas', 'search'],
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.widgets'),
        componentName: 'WidgetSidebarComponent',
        key: 'widgets',
        header: this.translateService.instant('sidebar-tools.label.widgets'),
        icon: ['fas', 'chart-bar'],
        permission: AppFeatures.WIDGETS_LIBRARY_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },

        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.filters'),
        componentName: 'FiltersComponent',
        key: 'filters',
        header: this.translateService.instant('sidebar-tools.label.filters'),
        icon: ['fas', 'filter'],
        permission: AppFeatures.FACET_FILTERS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.alerts'),
        componentName: 'AlertsComponent',
        header: this.translateService.instant('sidebar-tools.label.alerts'),
        key: 'alerts',
        icon: ['fas', 'bell'],
        permission: AppFeatures.ALERTS_MANAGER_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.export'),
        componentName: 'ExportComponent',
        header: this.translateService.instant('sidebar-tools.label.export'),
        key: 'export',
        icon: ['fas', 'share-nodes'],
        permission: AppFeatures.EXPORT_AND_SHARE_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
          shareMode: ShareMode.RESULTSET,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.schedules'),
        componentName: 'SchedulesComponent',
        header: this.translateService.instant('sidebar-tools.label.schedules'),
        key: 'schedules',
        icon: ['fas', 'clock-rotate-left'],
        permission: AppFeatures.EXPORT_AND_SHARE_SCHEDULES,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant(
          'sidebar-tools.label.scheduled-algorithms',
        ),
        componentName: 'AlgorithmsComponent',
        header: this.translateService.instant(
          'sidebar-tools.label.scheduled-algorithms',
        ),
        key: 'algorithms',
        icon: ['fas', 'microchip'],
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_edit'],
      },
    ];

    this.sidebarButtons = buttons.filter(
      (b) =>
        this.userFacade.hasAllObjectPermissionsSync(
          feed,
          b.objectPermission ?? [],
        ) &&
        (!b.permission ||
          this.userFacade.hasPermissionSync(b.permission) === true),
    );
    this.sidebarButtons$.next(this.sidebarButtons);
  }

  // ! Add grid change handler method
  onGridChange(event: any) {
    if (event && event.nodes && event.nodes.length > 0) {
      // Stage the new geometry and persist it.
      this.layoutStaging.stageReposition(
        event.nodes.map((node) => ({
          widgetId: node.id,
          x: node.x,
          y: node.y,
          cols: node.w,
          rows: node.h,
        })),
      );
    }
  }

  // * Estimates the pane class based on the number of views valid in the feed.
  estimatePaneClass(feed: Feed): void {
    const validViews = new Set(['list', 'details', 'analytics']);
    const paneClasses = ['pane-single', 'pane-double', 'pane-triple'];

    const viewSize =
      feed?.views?.reduce(
        (count, view) => (validViews.has(view) ? count + 1 : count),
        0,
      ) ?? 0;

    this.paneClass =
      viewSize > 0
        ? paneClasses[Math.min(viewSize - 1, paneClasses.length - 1)]
        : 'pane-default';
  }

  onDataChange(data) {
    this.logger.debug('Data changed', data);
  }

  setFeedEditMode(editMode: boolean) {
    this.store.dispatch(feedActions.setFeedEditMode({ editMode: editMode }));
  }

  loadComponent(sidebarButton: SidebarButtonConfig): void {
    this.sidebarPanelService.getChildInstance().loadComponent(sidebarButton);
  }

  onFormGroupChange(formGroup: FormGroup) {
    this.logger.debug('search builder formGroup', formGroup);
    const socialSourceIds =
      this.formService.extractFiltersFormDataPropertyValues(
        formGroup.value,
        'post_social_source_id',
      );

    this.logger.debug('socialSourceIds', socialSourceIds);
    this.searchBuilderData = formGroup.value;
    this.searchBuilderData.social_source_id = socialSourceIds;
  }

  setupRouteParamsHandling() {
    // Accessing query params
    this.activatedRoute.queryParams
      .pipe(
        tap((params) => {
          const startTimestamp = params['start_time'];
          const endTimestamp = params['end_time'];

          if (startTimestamp && endTimestamp) {
            this.store.dispatch(
              periodChanged({
                period: {
                  startTimestamp: startTimestamp,
                  endTimestamp: endTimestamp,
                },
              }),
            );
          }

          const page = params['page'];

          if (page) {
            // Assuming 'page' is a number
            this.store.dispatch(feedActions.pageChanged({ pageNumber: page }));
          }

          if (params['filters']) {
            // Assuming 'filters' is a JSON-encoded string of the array of filters
            try {
              const filters = JSON.parse(params['filters']);
              // TODO: dispatch an action to apply these filters once the
              // corresponding feed action exists — currently parsed but not used.
              this.logger.debug('Parsed filters from query params', filters);
            } catch (error) {
              this.logger.error('Error parsing filters', error);
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTogglePostDetails(
    $event: { isDetailsView: boolean; postId: string },
    index: number,
  ) {
    this.openPostDetails($event.postId, index);

    this.widgetService.widgetResized.emit();
    this.postList?.scrollToPost(index);
  }

  closePostDetails(): void {
    this.store.dispatch(openPostDetails({ postId: null }));
  }

  handleSearchStringWidgetChange(query: string) {
    // Use the debounced function instead of the original search function
    if (typeof query === 'string') {
      this.searchWidgets$.next(query);
    }
  }

  selectAllWidgets(selected: boolean) {
    if (selected) {
      const widgets = this.widgetList().filter(
        (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
      );
      this.store.dispatch(selectObjects({ objects: widgets }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }

  widgetConfigure() {
    // The service returns the flipped value - a cache patch does not update this
    // field synchronously the way a reducer would.
    if (this.widgetViewPreset.toggleEditMode()) {
      this.refreshWidgetList$.next();
    }
    setTimeout(() => {
      this.calculateCellHeight();
    });
  }

  widgetRefresh() {
    // Refetches every mounted, already-loaded widget card. No compact-view guard
    // needed: there are no mounted cards there, so it is already a no-op.
    this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());
  }

  feedConfigure() {
    this.store.dispatch(feedActions.toggleFeedEditMode());
  }

  feedRefresh() {
    this.feedPosts.invalidate();
  }

  openFeedSettings() {
    this.objectEditorService.loadFeedAndOpenPopup(this.feed.id);
  }

  private setupAutoRefresh(feed: Feed): void {
    clearInterval(this.intervalId);
    this.intervalId = null;

    if (!feed?.is_auto_refresh || !feed.auto_refresh_seconds) {
      return;
    }

    const intervalTime = feed.auto_refresh_seconds * 1000;
    this.intervalId = setInterval(() => {
      this.feedPosts.invalidate();
    }, intervalTime);
  }

  onPageChanged(page: number) {
    this.store.dispatch(feedActions.pageChanged({ pageNumber: page }));
  }

  onItemsPerPageChanged(itemsPerPage: number) {
    this.store.dispatch(feedActions.itemsPerPageChanged({ itemsPerPage }));
  }

  widgetTrackByFn(index: number, item: { widget: Widget }): string {
    return item.widget.id;
  }

  // * Reuses the cached options object when the widget's geometry is unchanged,
  // * so gridstack-item only sees a new input when something actually moved.
  private toGridOptions(widget: Widget): GridStackNode {
    const noResize =
      widget.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER;
    const cached = this.gridOptionsCache.get(widget.id);
    if (
      cached &&
      cached.x === widget.x &&
      cached.y === widget.y &&
      cached.w === widget.cols &&
      cached.h === widget.rows &&
      cached.noResize === noResize
    ) {
      return cached;
    }
    return {
      x: widget.x,
      y: widget.y,
      w: widget.cols,
      h: widget.rows,
      id: widget.id,
      noResize,
    };
  }

  postTrackByFn(index: number, post: Post): string {
    return post.id;
  }

  onWidgetListSorted(result: SortableResult): void {
    // * update local array
    this.widgetsSortableList = cloneDeep(result.items);

    const updatedWidgets = this.updateWidgetPositions(result.items);

    this.layoutStaging.stageResize(
      updatedWidgets.map((widget) => ({
        widgetId: widget.id,
        x: widget.x,
        y: widget.y,
        cols: widget.cols,
        rows: widget.rows,
      })),
    );
  }

  updateWidgetPositions(widgets: Widget[]): Widget[] {
    let currentY = 0;

    return widgets.map((widget) => {
      // Assign the current Y position to the widget
      const updatedWidget = { ...widget, y: currentY };
      // Update the current Y position for the next widget
      currentY += widget.rows;

      return updatedWidget;
    });
  }

  private handleIFrameMessage = (event: MessageEvent) => {
    const type = event.data?.type;

    if (type === 'widgetClick') {
      const record = event.data.payload;
      this.logger.debug('Widget click event received', record);
      this.store.dispatch(widgetActions.htmlWidgetClicked({ payload: record }));
      return;
    }

    if (type === 'widgetSortChange') {
      const { widget_id: widgetId, filter } = event.data.payload || {};
      if (!widgetId || !filter) {
        return;
      }

      const widget = this.widgetsQuery
        .data()
        ?.find((candidate) => candidate.id === widgetId);
      if (!widget) {
        return;
      }

      const updated = {
        ...widget,
        settings: {
          ...widget.settings,
          order_by_field: {
            ...(widget.settings?.order_by_field || {}),
            filter,
          },
        },
      };

      this.widgetPersistence.updateWidget(widgetId, updated);

      return;
    }

    if (type === 'share-video-segment') {
      const shareParams = event.data.payload;
      const playerUrl = shareParams.playerURL as string;

      const post = this.feedPosts.byVideoUrlTarget(playerUrl);

      if (!post) {
        this.logger.warn('No post found for video url', playerUrl, shareParams);

        this.toastService.showError(
          this.translateService.instant('video-share.error.post-not-found'),
        );
        return;
      }

      this.objectEditorService.sharePost(post, shareParams);

      return;
    }
  };

  // * Opens the details of a post.
  private openPostDetails(postId: string, index: number): void {
    this.store.dispatch(openPostDetails({ postId: postId }));

    this.feedViews.toggleView(this.feedId, 'details', true);
  }

  public remToPx(remStr) {
    const rem = parseFloat(remStr); // e.g., "1rem" -> 1
    const appRemPx = 14; // px
    return Math.round(rem * appRemPx); // use Math.floor/ceil if you prefer
  }

  // * Make Gridstack cell half the width of the cell
  private calculateCellHeight() {
    if (this.gridstack?.el) {
      const containerWidth = this.gridstack.el.getBoundingClientRect().width;
      const margin = this.remToPx(this.gridstackOptions.margin);
      const column = Number(this.gridstackOptions.column);

      const margins = margin * (column - 1);
      const columnWidth = (containerWidth - margins) / column;
      const cellHeight = columnWidth / 2;

      // * Update grid with new cellHeight
      this.gridstack.el.gridstack.cellHeight(cellHeight);
    }
  }

  selectSectionBreakWidget(clickedWidget: Widget): void {
    const widgets = [...this.widgetList()].sort((a, b) => a.y - b.y);

    const startIndex = widgets.findIndex((w) => w.id === clickedWidget.id);
    if (startIndex === -1) return;

    for (let i = startIndex + 1; i < widgets.length; i++) {
      const widget = widgets[i];

      if (widget.widget_type_id === WidgetType.WIDGET_TYPE_SECTION_BREAK) {
        // Stop if we hit another section break
        break;
      }

      // Only select widgets that are NOT section breaks
      this.objectActionService.toggleObjectSelection(widget);
    }
  }

  onRequestToggleThread({ postId }: { postId: string }) {
    const cmp = this.postList?.postCardCmps?.find((c) => c.post.id === postId);
    if (!cmp?.card) {
      this.logger.error(
        'Post card component or card element not found' + postId,
      );
      return;
    }

    const cardEl = cmp.card.nativeElement;
    const beforeTop = cardEl.getBoundingClientRect().top;

    this.feedPosts.toggleThread(postId);

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      const afterTop = cardEl.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;

      const scroller = this.postList?.cardContainer?.nativeElement ?? window;
      if (scroller instanceof HTMLElement) {
        scroller.scrollTop += delta;
      } else {
        window.scrollTo({ top: window.scrollY + delta });
      }
    });
  }

  onDragOver(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();
      event.dataTransfer!.dropEffect = 'copy';
    });
  }

  onDrop(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();

      // ! parse JSON outside zone
      const dragData = this.getDragDataOutsideZone(event);

      if (dragData?.type === 'widget-visualization-type') {
        // ! re-enter zone only for state updates
        this.ngZone.run(() => {
          this.createWidgetFromDrop(dragData);
        });
      }
    });
  }

  private createWidgetFromDrop(dragData: any): void {
    this.widgetPersistence.createWidget({
      chartId: dragData.chartId,
      chartTypeId: dragData.chartTypeId,
      objectId: this.feed.id,
      objectClassId: NomObjectClass.FEED,
      dataSourceObjectId: dragData.dataSourceObjectId,
      dataSourceObjectClassId: dragData.dataSourceObjectClassId,
    });
  }
  private setupFeedChangeListener(): void {
    this.actions$
      .pipe(
        ofType(feedActions.loadFeedSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.feed = action.feed;
          this.isStoryFeed =
            action.feed.object_type_id === NomObjectTypes.STORY_FEED;
          this.setupSidebar(action.feed);
          this.estimatePaneClass(action.feed);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }
  private setupWidgetCreationListener(): void {
    this.actions$
      .pipe(
        ofType(widgetActions.createWidgetSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.pendingScrollWidgetId = action.widget.id;
        }),
      )
      .subscribe();
  }

  private scrollToWidget(widgetId: string): void {
    const widgetElement = document.getElementById(`widget-${widgetId}`);
    const scrollContainer = document.querySelector('.card-container.widgets');

    if (widgetElement && scrollContainer) {
      // TODO: this is working only with block: 'nearest'
      // TODO: other options like 'center' are pushing the parents up
      // TODO: there are other ways to scroll using getBoundingClientRect and scrollTo
      widgetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  private getDragDataOutsideZone(event: DragEvent): any {
    try {
      const data = event.dataTransfer?.getData('application/json');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  }
}
